import { useMutation, type UseMutationReturnType } from '@tanstack/vue-query'
import { apiInstance, type ResponseType, type ReturnResponseFor } from '@/services/apiService'
import type { Response } from '@/types/response'
import type { MaybeRef } from 'vue'
import { toValue } from 'vue'

interface UseApiParams<TResponseType extends ResponseType> {
  modus: string
  headers?: Record<string, string>
  responseType?: TResponseType
  mutationKey?: MaybeRef<Record<string, unknown>>
}

export interface MutateAsyncVariables {
  method?: 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  payload?: Record<string, unknown>
  signal?: AbortSignal
}

// TODO: better typings + error handling
// maybe use generic type for response data
// and infer the response type based on modus
// e.g. if modus is 'user', then response type is User
// if modus is 'template', then response type is Template, etc.
// or use a map of modus to response type
// e.g. const modusResponseMap = { user: User, template: Template, ... }
// then use modusResponseMap[modus] to get the response type
// and use that type in the useApiMutation function
// e.g. useApiMutation<T = typeof modusResponseMap[modus]>({ modus, ... })
// This would require defining all the response types and the map
// which could be a lot of work but would provide strong typing
// and better developer experience
export function useApiMutation<Data = unknown>(
  params: UseApiParams<'json'>,
): UseMutationReturnType<Data, Error, MutateAsyncVariables, unknown>

// Overload 2: Non-JSON responses (blob, text, arrayBuffer)
export function useApiMutation<TResponseType extends Exclude<ResponseType, 'json'>>(
  params: UseApiParams<TResponseType>,
): UseMutationReturnType<ReturnResponseFor<TResponseType>, Error, MutateAsyncVariables, unknown>

export function useApiMutation<TResponseType extends ResponseType, Data = unknown>({
  modus,
  mutationKey,
  headers = {},
  responseType = 'json' as TResponseType,
}: UseApiParams<TResponseType>) {
  return useMutation<Data, Error, MutateAsyncVariables, unknown>({
    mutationFn: async (variables: MutateAsyncVariables) => {
      const apiService = apiInstance
      if (!apiService.baseUrl.startsWith('/') && !apiService.baseUrl.startsWith('http')) {
        apiService.baseUrl = '/' + apiService.baseUrl
      }
      const { method = 'POST', payload, signal } = variables
      const response = await apiService.request<
        typeof responseType,
        ReturnResponseFor<TResponseType, Response<Data>>
      >(modus, payload || {}, method, headers, responseType, signal)
      if (responseType === 'json') {
        const { Inhalt, Modus, Status } = response as Response<Data>
        if (Status === 'ok') {
          return Inhalt as Data
        }
        throw new Error(`${Status}|${Modus}|${Inhalt}` || 'Unknown error')
      } else {
        return response as Data
      }
    },
    mutationKey: [modus, toValue(mutationKey)],
  })
}
